import { useJobillaOrgId } from '@/hooks'
import { useAuth } from '@/lib'
import { useChat } from '@ai-sdk/react'
import {
	campaignsShowQueryKey,
	chatShowQueryKey,
	clients,
	mediaImageIndexQueryKey,
	useCampaignsShow,
} from '@jobilla/nexus-sdk'
import type { ChatStoreRequest } from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { ChatStatus, DefaultChatTransport, FileUIPart, UIMessage } from 'ai'
import i18next from 'i18next'
import { useAtomValue, useSetAtom } from 'jotai'
import { useCallback, useEffect, useRef, useState } from 'react'
import { TOOL_NAMES } from '../../data'
import { logger } from '../../ui'
import {
	activeChatToolsAtom,
	activeImageToolsAtom,
	CampaignFormQuestion,
	editorUserIsViewingAtom,
	formAtom,
} from '../../ui'
import { useAutoSendContinuation } from './-useAutoSendContinuation'
import { useChatHistory } from './-useChatHistory'
import { useToolStatusPolling } from './-useToolStatusPolling'

const extractErrorMessage = (_errorText: string): string => {
	// Return generic message - full error details are already captured by Sentry
	// and may contain sensitive(ish) info (like the URL of the chat endpoint (CAI))
	return 'An error occurred while processing your request. Please try again.'
}

// Format ad copy as human-readable text for AI tool response
const formatAdCopy = (ad: { title?: string; subtitle?: string; button?: string } | null) => {
	if (!ad) {
		return '(not set)'
	}
	return `• Title: "${ad.title ?? ''}"
• Subtitle: "${ad.subtitle ?? ''}"
• Call to Action: "${ad.button ?? ''}"`
}

// Format landing page content as human-readable text for AI tool response
const formatLandingPageContent = (
	welcomeScreen: {
		title?: string
		subtitle?: string
		description?: string
	} | null,
) => {
	if (!welcomeScreen) {
		return '(not set)'
	}
	return `• Title: "${welcomeScreen.title ?? ''}"
• Subtitle: "${welcomeScreen.subtitle ?? ''}"
• Description: "${welcomeScreen.description ?? ''}"`
}

const CONTACT_ATTRIBUTES: string[] = ['contact_consent', 'name', 'email', 'phone']

// Format survey/questionnaire content as human-readable text for AI tool response
const formatSurveyContent = (questions: CampaignFormQuestion[]) => {
	if (!questions || questions.length === 0) {
		return '(no questions)'
	}
	return questions.map((q, index) => {
		if (q.attribute && CONTACT_ATTRIBUTES.includes(q.attribute)) {
			return `${index + 1}. [SYSTEM — this question cannot be modified or removed]`
		}

		const optionsText = q.options && q.options.length > 0
			? `\n    Options:\n${
				q.options.map(o => {
					const score = q.scores?.find(s => s.optionId === o.id)?.scoreValue ?? '4'
					const disqualifying = o.isDisqualifying ? ', disqualifying' : ''
					return `      - "${o.label}" (score: ${score}${disqualifying})`
				}).join('\n')
			}`
			: ''
		return `${index + 1}. "${q.label}" (type: ${q.type})${optionsText}`
	}).join('\n')
}

// Generate deterministic UUID for deduplication
// Creates a UUID v5-style identifier from a string input
const generateDeterministicUUID = async (input: string): Promise<string> => {
	// Hash the input string
	const encoder = new TextEncoder()
	const data = encoder.encode(input)
	const hashBuffer = await crypto.subtle.digest('SHA-256', data)
	const hashArray = Array.from(new Uint8Array(hashBuffer))

	// Convert to hex string
	const hex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')

	// Format as UUID v5 (xxxxxxxx-xxxx-5xxx-yxxx-xxxxxxxxxxxx)
	// Set version (5) and variant bits
	return [
		hex.slice(0, 8),
		hex.slice(8, 12),
		'5' + hex.slice(13, 16), // Version 5
		((parseInt(hex.slice(16, 18), 16) & 0x3f) | 0x80).toString(16).padStart(2, '0') + hex.slice(18, 20), // Variant
		hex.slice(20, 32),
	].join('-')
}

export type UseChatEditorArgs = {
	campaignId: string
}

export type UseChatEditorReturns = {
	chat: {
		messages: UIMessage[]
		sendMessage: (message: { text: string; files?: FileList | FileUIPart[] }) => Promise<void>
		status: ChatStatus
		isLoading: boolean
		regenerate: () => Promise<void>
		stop: () => void
		resetChat: () => Promise<void>
		errorMessage: string | null
		hasPendingToolCalls: boolean
		chatId: string
	}
	isLoadingChatHistory: boolean
}

export function useChatEditor({
	campaignId,
}: UseChatEditorArgs): UseChatEditorReturns {
	const chatId = campaignId
	const queryClient = useQueryClient()
	const jobillaOrgId = useJobillaOrgId()
	const { getAccessToken } = useAuth()

	const [errorMessage, setErrorMessage] = useState<string | null>(null)
	const [isResettingChat, setIsResettingChat] = useState(false)
	// const [isPolling, setIsPolling] = useState(false)

	// Store current message context for PostHog tracking (accessible in onError)
	const posthogEventDataRef = useRef<
		{
			organisation_id: string
			campaign_id: string
			message_id: string | undefined
			message_type: string
			message_text_length: number
		} | null
	>(null)

	// Fetch initial chat history once
	const { messages: initialMessages, isLoadingChatHistory } = useChatHistory(chatId)

	const userIsViewing = useAtomValue(editorUserIsViewingAtom)
	const setActiveChatTools = useSetAtom(activeChatToolsAtom)
	const setActiveImageTools = useSetAtom(activeImageToolsAtom)

	// Fetch campaign data to get current state for tool calls
	const campaign = useCampaignsShow(campaignId, {
		staleTime: 3000,
	})

	// Use refs to capture fresh state on each message send (instead of stale closure state)
	const campaignDataRef = useRef<{ feed: any; story: any; welcomeScreen: any } | undefined>({
		feed: null,
		story: null,
		welcomeScreen: null,
	})
	const userViewingRef = useRef<string>('Feed ad')

	// Ref for form/survey state (questions)
	const formRef = useRef<{ questions: CampaignFormQuestion[] } | null>(null)

	// Access form atom for survey content
	const form = useAtomValue(formAtom)

	// Sync refs whenever campaign data or user viewing state changes
	useEffect(() => {
		if (campaign.data) {
			campaignDataRef.current = {
				feed: campaign.data.feed,
				story: campaign.data.story,
				welcomeScreen: campaign.data.welcomeScreen,
			}
		} else {
			campaignDataRef.current = undefined
		}

		userViewingRef.current = userIsViewing === 'feed'
			? 'Feed ad'
			: userIsViewing === 'story'
			? 'Story ad'
			: userIsViewing === 'landing'
			? 'Landing Page'
			: userIsViewing === 'form'
			? 'Form'
			: userIsViewing === 'thank-you'
			? 'Thank You Page'
			: userIsViewing
	}, [campaign.data, userIsViewing])

	// Sync formRef whenever form atom changes
	useEffect(() => {
		formRef.current = form ? { questions: form.questions } : null
	}, [form])

	const { messages, status, sendMessage: originalSendMessage, regenerate, stop, setMessages, addToolOutput } =
		useChat({
			transport: new DefaultChatTransport({
				api: import.meta.env.VITE_NEXUS_API_URL + '/chat',
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'application/json',
				},
				// The AI SDK types a transport body as `object`, so nothing checks
				// these keys against the endpoint. Pinned to the generated request
				// type: this body previously sent `campaign_id` and every send 422'd.
				body: { campaignId } satisfies Pick<ChatStoreRequest, 'campaignId'>,
			}),
			id: chatId,
			messages: initialMessages,
			// Auto-continuation is handled explicitly in useAutoSendContinuation.
			sendAutomaticallyWhen: () => false,
			onError: (error: Error) => {
				const errorMsg = extractErrorMessage(error.message || 'Chat request failed')
				setErrorMessage(errorMsg)
				stop()

				logger.track('Chat: Message sending failed', {
					data: {
						...(posthogEventDataRef.current || {}),
						error_message: error.message || 'Chat request failed',
					},
				})
			},
			onToolCall: async ({ toolCall }) => {
				// Frontend-executed tools: provide current UI state via addToolOutput.
				// Backend tools: do nothing here, they're handled server-side.
				if (toolCall.toolName === TOOL_NAMES.getAdCopy) {
					const feed = campaignDataRef.current?.feed
					const story = campaignDataRef.current?.story

					addToolOutput({
						tool: TOOL_NAMES.getAdCopy,
						toolCallId: toolCall.toolCallId,
						output: `Currently visible ad copies:
Feed Ad:
${formatAdCopy(feed)}
Story Ad:
${formatAdCopy(story)}`,
					})
				}

				if (toolCall.toolName === TOOL_NAMES.getLandingPageContent) {
					const welcomeScreen = campaignDataRef.current?.welcomeScreen

					addToolOutput({
						tool: TOOL_NAMES.getLandingPageContent,
						toolCallId: toolCall.toolCallId,
						output: `Current landing page content:
${formatLandingPageContent(welcomeScreen)}`,
					})
				}

				if (toolCall.toolName === TOOL_NAMES.getSurveyContent) {
					const currentQuestions = formRef.current?.questions || []
					addToolOutput({
						tool: TOOL_NAMES.getSurveyContent,
						toolCallId: toolCall.toolCallId,
						output: `Current questionnaire questions:
${formatSurveyContent(currentQuestions)}`,
					})
				}
			},
		})

	// Build extra_data with current state (defined early for use in callbacks)
	const buildExtraData = useCallback(() => {
		const jobDetails = {
			jobTitle: campaign.data?.title ?? 'Lead Developer',
			jobDescription: campaign.data?.requirements
				?? 'We are looking for a Lead Developer to join our team.',
			jobLocation: campaign.data?.location ?? 'Helsinki',
			targetAudience: 'candidates',
			language: campaign.data?.language ?? 'en-GB',
		}

		const brandParameters = {
			toneOfVoice: campaign.data?.branding?.toneOfVoice
				|| 'Professional yet approachable and forward-thinking.',
			visualGuidance: campaign.data?.branding?.visualGuidance
				|| 'Modern, inclusive, and vibrant visual design.',
		}

		return {
			userViewing: userViewingRef.current,
			selectedImageIds: {
				feed: campaignDataRef.current?.feed?.image_id ?? '',
				story: campaignDataRef.current?.story?.image_id ?? '',
				landing: campaignDataRef.current?.welcomeScreen?.image_id ?? '',
			},
			campaignDetails: {
				id: campaignId,
				jobTitle: jobDetails.jobTitle,
				jobDescription: jobDetails.jobDescription,
				jobLocation: jobDetails.jobLocation,
				language: jobDetails.language,
			},
			jobDetails,
			brandParameters,
			// Live content snapshots (survey, ad copy, landing page, image) are built
			// server-side by CampaignSnapshotService from the DB — single source of truth.
		}
	}, [campaign.data, campaignId])

	// Track whether we've synced initial messages to prevent overwriting streamed messages
	const hasSyncedInitialMessagesRef = useRef(false)

	// Callbacks for tool status polling
	const handleStartPolling = useCallback(() => {
		// console.debug('[useChatEditor] Polling started')
		// setIsPolling(true)
	}, [])

	const handleStopPolling = useCallback(() => {
		// console.debug('[useChatEditor] Polling stopped')
		// setIsPolling(false)
	}, [])

	const handleToolStateChange = useCallback((updatedMessages: UIMessage[]) => {
		// console.debug('[useChatEditor] Tool states updated, syncing messages')
		setMessages(updatedMessages)
	}, [setMessages])

	const handleToolsCompleted = useCallback(() => {
		// console.debug('[useChatEditor] All tools completed - invalidating cache')

		// Invalidate campaign and media queries when tools complete
		void queryClient.invalidateQueries({
			queryKey: campaignsShowQueryKey(campaignId),
		})
		void queryClient.invalidateQueries({
			queryKey: mediaImageIndexQueryKey(),
		})
	}, [campaignId, queryClient])

	const handleToolTriggered = useCallback((toolName: string, messageId: string) => {
		// console.debug('[useChatEditor] Tool triggered:', toolName)
		// Generate deterministic UUID for deduplication (prevents duplicates on page reload)
		void generateDeterministicUUID(`tool-triggered-${campaignId}-${messageId}-${toolName}`).then(insertId => {
			logger.track('Chat: Tool triggered', {
				data: {
					$insert_id: insertId,
					organisation_id: jobillaOrgId,
					campaign_id: campaignId,
					message_id: messageId,
					tool_name: toolName,
				},
			})
		}).catch((err) => logger.warn('Tracking pipeline failed', { data: { error: err } }))
	}, [campaignId, jobillaOrgId])

	const handleToolCompleted = useCallback((toolName: string, messageId: string, success: boolean) => {
		// console.debug('[useChatEditor] Tool completed:', toolName, success ? 'success' : 'error')
		// Generate deterministic UUID for deduplication (prevents duplicates on page reload)
		void generateDeterministicUUID(`tool-completed-${campaignId}-${messageId}-${toolName}-${success}`).then(
			insertId => {
				logger.track('Chat: Tool completed', {
					data: {
						$insert_id: insertId,
						organisation_id: jobillaOrgId,
						campaign_id: campaignId,
						message_id: messageId,
						tool_name: toolName,
						success,
					},
				})
			},
		).catch((err) => logger.warn('Tracking pipeline failed', { data: { error: err } }))
	}, [campaignId, jobillaOrgId])

	// Tool status polling with callbacks
	const {
		hasPendingTools: hasPendingToolCalls,
		activeToolExecution,
		activeImageToolExecution,
		errorMessage: toolErrorMessage,
	} = useToolStatusPolling({
		chatId,
		messages,
		onStartPolling: handleStartPolling,
		onStopPolling: handleStopPolling,
		onToolStateChange: handleToolStateChange,
		onToolsCompleted: handleToolsCompleted,
		onToolTriggered: handleToolTriggered,
		onToolCompleted: handleToolCompleted,
	})

	/**
	 * CRITICAL: Initial message synchronization with race condition prevention
	 *
	 * This effect syncs initial chat history from the server into useChat's state exactly once.
	 * After this initial sync, all subsequent message updates flow through:
	 * 1. useChat (streaming responses during active chat)
	 * 2. useToolStatusPolling (tool execution status updates)
	 * 3. useAutoSendContinuation (automatic continuation messages)
	 *
	 * Race Condition Prevented:
	 * Without the one-time sync guard, this sequence would corrupt message state:
	 * - User sends message → useChat starts streaming assistant response
	 * - While streaming, initial history finishes loading (async)
	 * - History sync overwrites messages array → streaming message is lost
	 *
	 * Solution:
	 * - Use ref (not state) to track sync status - prevents effect re-runs and React re-renders
	 * - Guard ensures we sync only once when chat first loads, never again
	 * - After initial sync, useChat owns the message state completely
	 *
	 * Note: eslint-disable is intentional - we need messages.length in the dependency array
	 * to handle edge cases, but ESLint's exhaustive-deps rule suggests excluding it.
	 * The ref guard ensures we never re-sync even if dependencies change.
	 */
	useEffect(() => {
		if (hasSyncedInitialMessagesRef.current) {
			return // Already synced, never sync again to prevent overwriting streamed messages
		}

		if (isLoadingChatHistory) {
			return // Wait for history to load
		}

		// Sync initial messages once they're loaded
		if (initialMessages.length > 0) {
			setMessages(initialMessages)
			hasSyncedInitialMessagesRef.current = true
		}
	}, [isLoadingChatHistory, initialMessages, messages.length, setMessages])

	// Auto-send continuation when last message has completed tool calls (and no text after)
	useAutoSendContinuation({
		messages,
		isLoadingChatHistory,
		status,
		hasPendingToolCalls,
		isResettingChat,
		campaignId,
		buildExtraData,
		originalSendMessage,
		getAccessToken,
	})

	// Sync tool error messages to local error state
	useEffect(() => {
		if (toolErrorMessage) {
			setErrorMessage(toolErrorMessage)
		}
	}, [toolErrorMessage])

	// Update tool status atoms
	useEffect(() => {
		setActiveChatTools(activeToolExecution)
		setActiveImageTools(activeImageToolExecution)
	}, [activeToolExecution, activeImageToolExecution, setActiveChatTools, setActiveImageTools])

	// Wrap sendMessage to clear previous error state on new attempts and block while tools run
	const sendMessage: typeof originalSendMessage = async (message, options) => {
		if (hasPendingToolCalls) {
			setErrorMessage('A tool is still running. Please wait until it finishes.')
			return
		}

		setErrorMessage(null)

		const token = await getAccessToken()
		const authHeaders = {
			'Accept': 'application/json',
			'Content-Type': 'application/json',
			'Authorization': `Bearer ${token}`,
		}

		const extraData = buildExtraData()
		const optionsWithExtraData = {
			...(options || {}),
			body: {
				...(options?.body || {}),
				extraData,
			},
			headers: authHeaders,
		}

		const messageText = message && 'text' in message ? message.text : ''
		const hasText = Boolean(messageText)
		const hasAttachments = message && 'files' in message ? Boolean(message.files?.length) : false
		const posthogEventData = {
			organisation_id: jobillaOrgId,
			campaign_id: campaignId,
			message_id: message && 'id' in message ? message.id : '',
			message_type: hasAttachments ? (hasText ? 'text_and_attachments' : 'attachments_only') : 'text_only',
			message_text_length: messageText?.length ?? 0,
			message_index: messages.length,
			user_is_viewing: userViewingRef.current,
			ui_language: i18next.language,
			chat_id: chatId,
		}

		// Store in ref so onError can access fresh data
		posthogEventDataRef.current = posthogEventData

		return originalSendMessage(message, optionsWithExtraData).then(() => {
			logger.track('Chat: Message sent', {
				data: posthogEventDataRef.current ?? posthogEventData,
			})
		})
	}

	// Wrap regenerate to include extra_data
	const wrappedRegenerate = async () => {
		if (hasPendingToolCalls) {
			setErrorMessage('A tool is still running. Please wait until it finishes.')
			return
		}

		setErrorMessage(null)

		const token = await getAccessToken()
		const authHeaders = {
			'Accept': 'application/json',
			'Content-Type': 'application/json',
			'Authorization': `Bearer ${token}`,
		}

		const extraData = buildExtraData()

		logger.track('Chat: Regenerate', {
			data: {
				organisation_id: jobillaOrgId,
				campaign_id: campaignId,
				chat_id: chatId,
				message_count: messages.length,
				user_is_viewing: userViewingRef.current,
			},
		})

		return regenerate({
			body: {
				extraData,
			},
			headers: authHeaders,
		}).catch((error: Error) => {
			setErrorMessage(error.message || 'Failed to regenerate message')
			throw error
		})
	}

	// Reset chat: flush conversation history while preserving campaign context
	const resetChatFn = useCallback(async () => {
		setIsResettingChat(true)
		// Snapshot before clearing so we can restore if the backend call fails.
		const previousMessages = messages
		try {
			// CRITICAL: stop() and setMessages([]) must happen in the same synchronous
			// block BEFORE any await. Otherwise, stop() changes status to 'ready' and
			// React re-renders — useAutoSendContinuation sees completed tool calls in
			// the still-populated messages array and fires a continuation, creating an
			// infinite tool-call loop.
			stop()
			setMessages([])
			setErrorMessage(null)
			setActiveChatTools({ feedAd: false, storyAd: false, landingPage: false, form: false })
			setActiveImageTools({ feedAd: false, storyAd: false, landingPage: false, form: false })

			// Now do the async backend call
			const token = await getAccessToken()
			await clients.chatResetChat(chatId, {
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`,
				},
			})

			// Invalidate chat history query so useChatHistory refetches (empty post-reset)
			void queryClient.invalidateQueries({
				queryKey: chatShowQueryKey(chatId),
			})

			logger.track('Chat: Reset', {
				data: {
					organisation_id: jobillaOrgId,
					campaign_id: campaignId,
					chat_id: chatId,
				},
			})
		} catch (error) {
			// Backend reset failed — restore messages so the UI isn't left empty.
			setMessages(previousMessages)
			const message = error instanceof Error ? error.message : 'Failed to reset chat'
			setErrorMessage(message)
			throw error
		} finally {
			setIsResettingChat(false)
		}
	}, [
		chatId,
		campaignId,
		getAccessToken,
		messages,
		stop,
		setMessages,
		setActiveChatTools,
		setActiveImageTools,
		queryClient,
		jobillaOrgId,
	])

	const lastMessage = messages.at(-1)

	// Show loading indicator when:
	// 1. Message is submitted, OR
	// 2. Message is streaming AND last message is assistant AND (no parts yet OR last part is step-start)
	const isLoading = status === 'submitted' || (status === 'streaming'
		&& lastMessage?.role === 'assistant'
		&& (lastMessage?.parts?.length === 0 || lastMessage?.parts?.at(-1)?.type === 'step-start'))

	return {
		chat: {
			messages,
			sendMessage,
			status,
			isLoading,
			regenerate: wrappedRegenerate,
			stop,
			resetChat: resetChatFn,
			errorMessage,
			hasPendingToolCalls,
			chatId,
		},
		isLoadingChatHistory,
	}
}
